import { Dispatch, SetStateAction, useState } from "react";
import { toast } from "react-toastify";
import { twMerge } from "tailwind-merge";
import { StatusAgendamentoEnum } from "../../../enums/statusAgendamento";
import { RolesEnum } from "../../../enums/roles";
import { RecursosEnum } from "../../../enums/recusos";
import { TipoAtendimentoEnum } from "../../../enums/tipoAtendimento";
import { IAgendamentoDTO } from "../../../types/agendamento";
import { Agendamento } from "../../../services/Agendamento";
import { FormatFileSize } from "../../../utils/FormatFileSize";
import { ButtonV2 } from "../ButtonV2";
import { MyModal } from "../MyModal";
import { Text } from "../Text";
import { FileContainer } from "../FileContainer";
import InputSingleFile from "../InputSingleFile";
import { useButtonOptions } from "../ButtonOptions/useButtonOptions";
import { useReagendarAgendamento } from "../../../hooks/useReagendarAgendamento";
import { IModalReembolsoV2Props } from "../ButtonOptionsV2";

interface IButtonOptionsV3Props {
  agendamento: IAgendamentoDTO;
  handlePix: () => void;
  handleConfirmPayment: () => void;
  handleReturnStatus: () => void;
  handleConfirmRefund: Dispatch<SetStateAction<IModalReembolsoV2Props>>;
  onFinalize?: () => void;
}

/**
 * Actions dropdown for the agendamento details page — same menu options,
 * visibility rules and handlers as `ButtonOptionsV2`, with the trigger
 * restyled to the details-view Figma (light pill with "more" icon + chevron).
 * `ButtonOptionsV2` is kept untouched; use this one on the card-based details
 * view only.
 */
export const ButtonOptionsV3 = ({
  agendamento,
  handlePix,
  handleConfirmPayment,
  handleReturnStatus,
  handleConfirmRefund,
  onFinalize,
}: IButtonOptionsV3Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const { canReagendar, onRescheduling } =
    useReagendarAgendamento(agendamento);

  const {
    isIntern,
    navigate,
    setIsLoad,
    sessionAgendamento,
    handleCancel,
    usuario,
    recursos,
    modalFinalizar,
    setModalFinalizar,
    laudoFile,
    setLaudoFile,
    handleCloseFinalizar,
    handleFinalizarManualmente,
  } = useButtonOptions();

  const usuarioLogado = JSON.parse(
    localStorage.getItem("@usuarioMapa") || "{}",
  );

  const isUserOwnerOfFinishedAppointment =
    agendamento?.cliente?.uuidUsuario === usuarioLogado?.uuidUsuario &&
    agendamento?.status === StatusAgendamentoEnum?.FINALIZADO;

  function closeMenu() {
    setIsMenuOpen(false);
  }

  function handleSatisfactionSurvey() {
    if (!agendamento?.uuid) return;

    setIsLoad(true);

    Agendamento.getUrlPesquisa(agendamento.uuid)
      .then(({ data }) => {
        window.open(data, "_blank");
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          toast.error(mensagem);
        },
      )
      .finally(() => {
        setIsLoad(false);
      });
  }

  function handleNotifyArrival() {
    setIsLoad(true);
    Agendamento.comunicarChegada({ uuid: agendamento?.uuid })
      .then(() => {
        toast.success("Chegada comunicada com sucesso!");
      })
      .catch(() => {
        toast.error("Erro ao comunicar chegada");
      })
      .finally(() => {
        setIsLoad(false);
      });
  }

  const canConfirmPagamento =
    agendamento.status === StatusAgendamentoEnum.AGUARDANDO_PAGAMENTO &&
    (usuario?.roles?.includes(RolesEnum.ROLE_ADMIN) ||
      recursos?.includes(RecursosEnum.CONFIRMAR_PAGAMENTO));

  const canReturnStatus =
    agendamento.status === StatusAgendamentoEnum.INICIADO &&
    (usuario?.roles?.includes(RolesEnum.ROLE_ADMIN) ||
      recursos?.includes(RecursosEnum.RETORNAR_STATUS));

  const isAgendadoOuIniciado =
    agendamento.status === StatusAgendamentoEnum.INICIADO ||
    agendamento.status === StatusAgendamentoEnum.AGENDADO;

  const canComunicarAtraso =
    isAgendadoOuIniciado &&
    (usuario?.roles?.includes(RolesEnum.ROLE_ADMIN) ||
      recursos?.includes(RecursosEnum.COMUNICAR_ATRASO));

  const canComunicarChegada =
    isAgendadoOuIniciado &&
    (usuario?.roles?.includes(RolesEnum.ROLE_ADMIN) ||
      recursos?.includes(RecursosEnum.COMUNICAR_CHEGADA));

  const canRegistrarImpedimento =
    isAgendadoOuIniciado &&
    (usuario?.roles?.includes(RolesEnum.ROLE_ADMIN) ||
      recursos?.includes(RecursosEnum.REGISTRAR_IMPEDIMENTO));

  const canConfirmarReembolso =
    sessionAgendamento?.roles?.includes(RolesEnum.ROLE_ADMIN) ||
    recursos?.includes("CONFIMAR_REEMBOLSO");

  const canFinalizarManualmente =
    usuario?.roles?.includes(RolesEnum.ROLE_ADMIN) ||
    recursos?.includes(RecursosEnum.FINALIZAR_MANUAL);

  const canAlterarEnderecoAtendimento =
    agendamento.tipoAtendimento === TipoAtendimentoEnum.DOMICILIO &&
    !agendamento.concessionaria;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsMenuOpen((prev) => !prev)}
        className="flex h-11 items-center justify-center gap-2.5 rounded-full border border-solid border-[#e5e5ea] bg-[#fafcfe] px-5 font-['Mulish'] text-base font-normal text-[#2d2d2d]"
      >
        <img src="/assets/svgs/icon-more-horizontal.svg" alt="" className="h-6 w-6" />
        Ações
        <img
          src="/assets/svgs/arrowDownSelect.svg"
          alt=""
          className={twMerge(
            "h-6 w-6 transition-transform duration-200",
            isMenuOpen && "rotate-180",
          )}
        />
      </button>

      {isMenuOpen && (
        <div className="absolute left-0 top-[calc(100%+8px)] z-20 flex w-64 flex-col rounded-[10px] border border-solid border-[#e5e5ea] bg-white p-2 shadow-[0px_8px_24px_0px_rgba(0,0,0,0.078)]">
          {canConfirmPagamento && (
            <ButtonV2
              variant="text"
              className="h-auto w-full min-w-0 justify-start p-2 font-normal"
              onClick={() => {
                handleConfirmPayment();
                closeMenu();
              }}
            >
              Confirmar pagamento
            </ButtonV2>
          )}

          {canReturnStatus && (
            <ButtonV2
              variant="text"
              className="h-auto w-full min-w-0 justify-start p-2 font-normal"
              onClick={() => {
                handleReturnStatus();
                closeMenu();
              }}
            >
              Retornar status
            </ButtonV2>
          )}

          {canComunicarAtraso && (
            <ButtonV2
              variant="text"
              className="h-auto w-full min-w-0 justify-start p-2 font-normal"
              onClick={() => {
                navigate(`/agendamento/${agendamento?.uuid}/comunicar-atraso`);
                closeMenu();
              }}
            >
              Comunicar Atraso
            </ButtonV2>
          )}

          {canComunicarChegada && (
            <ButtonV2
              variant="text"
              className="h-auto w-full min-w-0 justify-start p-2 font-normal"
              onClick={() => {
                handleNotifyArrival();
                closeMenu();
              }}
            >
              Comunicar Chegada
            </ButtonV2>
          )}

          {canRegistrarImpedimento && (
            <ButtonV2
              variant="text"
              className="h-auto w-full min-w-0 justify-start p-2 font-normal"
              onClick={() => {
                navigate(
                  `/agendamento/${agendamento?.uuid}/registrar-impedimento`,
                );
                closeMenu();
              }}
            >
              Registrar Impedimento
            </ButtonV2>
          )}

          {canReagendar && (
            <ButtonV2
              variant="text"
              className="h-auto w-full min-w-0 justify-start p-2 font-normal"
              onClick={() => {
                onRescheduling();
                closeMenu();
              }}
            >
              Reagendar
            </ButtonV2>
          )}

          <ButtonV2
            variant="text"
            className="h-auto w-full min-w-0 justify-start p-2 font-normal"
            onClick={() => {
              setIsCancelModalOpen(true);
              closeMenu();
            }}
          >
            Cancelar
          </ButtonV2>

          {agendamento.status === StatusAgendamentoEnum.PAGO && (
            <ButtonV2
              variant="text"
              className="h-auto w-full min-w-0 justify-start p-2 font-normal"
              onClick={() => {
                navigate(`/agendamento/${agendamento?.uuid}/confirmar-horario`);
                closeMenu();
              }}
            >
              Realizar Agendamento
            </ButtonV2>
          )}

          {agendamento.status === StatusAgendamentoEnum.AGUARDANDO_PAGAMENTO && (
            <ButtonV2
              variant="text"
              className="h-auto w-full min-w-0 justify-start p-2 font-normal"
              onClick={() => {
                handlePix();
                closeMenu();
              }}
            >
              Ver fatura
            </ButtonV2>
          )}

          {canConfirmarReembolso && (
            <ButtonV2
              variant="text"
              className="h-auto w-full min-w-0 justify-start p-2 font-normal"
              onClick={() => {
                handleConfirmRefund({ isOPen: true });
                closeMenu();
              }}
            >
              Confirmar Reembolso
            </ButtonV2>
          )}

          <ButtonV2
            href={`/assets/docs/checklist-${process.env.REACT_APP_PROJECT}.pdf`}
            download
            variant="text"
            className="h-auto w-full min-w-0 justify-start p-2 font-normal"
            onClick={closeMenu}
          >
            Baixar Checklist
          </ButtonV2>

          {isUserOwnerOfFinishedAppointment && (
            <ButtonV2
              variant="text"
              className="h-auto w-full min-w-0 justify-start p-2 font-normal"
              onClick={() => {
                handleSatisfactionSurvey();
                closeMenu();
              }}
            >
              Avaliar
            </ButtonV2>
          )}

          {canFinalizarManualmente && (
            <ButtonV2
              variant="text"
              className="h-auto w-full min-w-0 justify-start p-2 font-normal"
              onClick={() => {
                setModalFinalizar({ open: true });
                closeMenu();
              }}
            >
              Finalizar
            </ButtonV2>
          )}

          {canAlterarEnderecoAtendimento && (
            <ButtonV2
              variant="text"
              className="h-auto w-full min-w-0 justify-start p-2 font-normal"
              onClick={() => {
                navigate(`/agendamento/${agendamento?.uuid}/alterar-endereco`);
                closeMenu();
              }}
            >
              Alterar Local de Atendimento
            </ButtonV2>
          )}
        </div>
      )}

      <MyModal isOpen={modalFinalizar.open} onRequestClose={handleCloseFinalizar}>
        <div className="flex w-full max-w-[90%] flex-col gap-8 p-4 md:min-w-[576px] md:max-w-[576px] md:p-6">
          <h1 className="text-xl font-semibold">Finalizar Manualmente</h1>

          <p>Anexe o laudo da vistoria realizada.</p>

          <div>
            {!laudoFile ? (
              <InputSingleFile onChange={(file) => setLaudoFile(file)} />
            ) : (
              <FileContainer
                name={laudoFile.name}
                size={FormatFileSize(laudoFile.size, 2)}
                handleClick={() => setLaudoFile(null)}
                containerClassName="rounded border bg-[#F6F6F9] border-solid border-[rgba(0,0,0,0.16)] justify-between"
              />
            )}

            <div className="mt-6 flex items-center justify-end gap-4">
              <ButtonV2
                variant="text"
                type="button"
                onClick={handleCloseFinalizar}
              >
                Cancelar
              </ButtonV2>
              <ButtonV2
                type="button"
                disabled={!laudoFile}
                onClick={() => {
                  if (laudoFile)
                    handleFinalizarManualmente(agendamento, laudoFile, onFinalize);
                }}
              >
                Confirmar
              </ButtonV2>
            </div>
          </div>
        </div>
      </MyModal>

      <MyModal
        isOpen={isCancelModalOpen}
        onRequestClose={() => setIsCancelModalOpen(false)}
      >
        <div className="flex w-full max-w-[90%] flex-col gap-6 p-4 md:min-w-[480px] md:max-w-[480px] md:p-6">
          <div className="flex items-start justify-between">
            <Text>
              Tem certeza que deseja{" "}
              <span className="font-bold">cancelar</span> sua vistoria?
            </Text>
            <button
              type="button"
              onClick={() => setIsCancelModalOpen(false)}
              aria-label="Fechar"
            >
              X
            </button>
          </div>

          <div>
            <h4 className="font-bold">Reembolso via PIX</h4>
            <p>
              Agendamentos pagos via <span className="font-bold">PIX</span> o
              reembolso acontecerá em{" "}
              <span className="font-bold">até 72h</span>.
            </p>

            <h4 className="mt-4 font-bold">Reembolso via Boleto</h4>
            <p>
              Agendamentos pagos via{" "}
              <span className="font-bold">Boleto</span> terão reembolso em{" "}
              <span className="font-bold">até 72h após compensação</span> com
              solicitação via preenchimento de formulário.
            </p>

            <div className="mt-6 flex items-center justify-end gap-4">
              <ButtonV2
                variant="text"
                onClick={() => setIsCancelModalOpen(false)}
              >
                Cancelar
              </ButtonV2>
              <ButtonV2
                onClick={() => {
                  handleCancel(agendamento);
                  setIsCancelModalOpen(false);
                }}
              >
                Confirmar
              </ButtonV2>
            </div>
          </div>
        </div>
      </MyModal>
    </div>
  );
};
